import {
  UserLanguage,
  type AccountEntityExpandResponse,
} from "../../../../generated/ExpHubApi";

// Web store mapping by country
const WebStoreMapping = {
  DE: {
    webstoreExternalId: "westfalen-de",
    salesOrganizationId: "1000",
    name: "Westfalen DE",
    webstoreId: "ws-1",
  },
  CH: {
    webstoreExternalId: "westfalen-ch",
    salesOrganizationId: "0078",
    name: "Westfalen CH",
    webstoreId: "ws-2",
  },
  NL: {
    webstoreExternalId: "westfalen-nl",
    salesOrganizationId: "0070",
    name: "Westfalen NL",
    webstoreId: "ws-3",
  },
  GG_DE: {
    webstoreExternalId: "globalgas-de",
    salesOrganizationId: "1000",
    name: "Globalgas-Store-DE",
    webstoreId: "ws-4",
  },
} as const;

const defaultSelectedCustomer: AccountEntityExpandResponse = {
  user: {
    userId: "1",
    name: "Emil E-Auto",
    email: "emil@example.com",
    language: UserLanguage.De,
  },
  contact: {
    contactId: "contact-1",
    name: "Klaus Kontakt",
  },
  account: {
    name: "Musterfirma GmbH",
    accountId: "0012500000i0qIPAAY",
    default: true,
    services: [],
    businessAreasSummary: [
      "WCC",
      "GAAS",
      "WESTFALEN.ID",
      "CUSTOMERDATA",
      "EQUIVOLT",
      "GHG",
      "MOBILITY",
      "B2BAPP",
      "WSC",
      "FBS",
      "GASESSHOP",
    ],
    webstores: [WebStoreMapping.DE, WebStoreMapping.GG_DE],
    customerType: "Kunde_B2C",
    isManaged: true,
  },
};

const defaultAddresses: NonNullable<AccountEntityExpandResponse["addresses"]> =
  [
    {
      kind: "SHIPPING",
      default: false,
      addressId: "address1",
      name1: "Zu Hause",
      street: "Kesselstr.",
      streetNumber: "40",
      postalCode: "70327",
      city: "Stuttgart",
      countryCode: "DE",
      salesOrganizationId: "1000",
      recipient: {
        firstName: "Martin",
        lastName: "Müller",
        email: "m.mueller@example.com",
        phone: "+491701234567",
      },
      openingHours: [
        {
          dayOfWeek: "MONDAY",
          dayTime: "MORNING",
          startTime: "08:00:00",
          endTime: "12:00:00",
        },
        {
          dayOfWeek: "MONDAY",
          dayTime: "AFTERNOON",
          startTime: "13:00:00",
          endTime: "17:00:00",
        },
        {
          dayOfWeek: "TUESDAY",
          dayTime: "MORNING",
          startTime: "09:00:00",
          endTime: "16:00:00",
        },
        {
          dayOfWeek: "FRIDAY",
          dayTime: "MORNING",
          startTime: "09:00:00",
          endTime: "16:00:00",
        },
      ],
      restrictions: {
        vehicle: true,
        forklift: true,
      },
      addressInfo3: "Bitte klingeln bei Firma XY",
    },
    {
      kind: "SHIPPING",
      default: true,
      addressId: "address2",
      name1: "Büro",
      street: "Hauptstraße",
      streetNumber: "45",
      postalCode: "54321",
      city: "Berlin",
      countryCode: "DE",
      salesOrganizationId: "1000",
    },
    {
      kind: "SHIPPING",
      name1: "Großeltern",
      addressId: "address3",
      default: false,
      street: "Omaweg",
      streetNumber: "32",
      postalCode: "67890",
      city: "Hamburg",
      countryCode: "DE",
      salesOrganizationId: "1000",
    },
    {
      kind: "BILLING",
      default: true,
      addressId: "address1",
      name1: "Zu Hause",
      street: "Kesselstr.",
      streetNumber: "40",
      postalCode: "70327",
      city: "Stuttgart",
      countryCode: "DE",
      salesOrganizationId: "1000",
    },
  ];

const defaultContactAddresses: NonNullable<
  AccountEntityExpandResponse["contactAddresses"]
> = [
  {
    kind: "INVOICE",
    email: "max.mustermann@billing.com",
    name1: "Mustermann",
    name2: "Max",
    partnerId: "partner-1",
  },
  {
    kind: "DELIVERY_NOTE",
    email: "eva.flaschengas@delivery.com",
    name1: "Flaschengas",
    name2: "Eva",
    partnerId: "partner-2",
  },
  {
    kind: "DELIVERY_NOTE",
    email: "sabine@delivery.com",
    name1: "Sabine Tisch",
    name2: "",
    partnerId: "partner-3",
  },
];

const services: NonNullable<
  AccountEntityExpandResponse["account"]["services"]
> = [
  {
    serviceId: "a279b0000013e5BAAQ",
    serviceExternalId: "gasesShop",
    subscription: {
      subscriptionId: "a269b000001VQvJAAW",
      name: "ServiceSub-040796",
    },
    serviceRoles: [
      {
        serviceRoleId: "a259b0000077Mm9AAE",
        serviceRoleExternalId: "userStoreman",
        assignment: {
          assignmentId: "a249b00000H2C9lAAF",
          name: "Service R Assignm-058291",
        },
      },
    ],
  },
  {
    serviceId: "a279b000001wpETAAY",
    serviceExternalId: "globalgasesshop",
    subscription: {
      subscriptionId: "a269b000001VQvWOA",
      name: "ServiceSub-040300",
      minimalAmountOfPallets: 2,
    },
    serviceRoles: [
      {
        serviceRoleId: "a259b00000BTf1RAAT",
        serviceRoleExternalId: "globalGasStoreman",
        assignment: {
          assignmentId: "a249b00000H2C9lABC",
          name: "Service R Assignm-058292",
        },
      },
    ],
  },
];

// Types for the options that can be passed to the function
type AddressInput = Partial<
  NonNullable<AccountEntityExpandResponse["addresses"]>[0]
> & {
  addressId: string;
  kind: "SHIPPING" | "BILLING";
};

type ContactAddressInput = Partial<
  NonNullable<AccountEntityExpandResponse["contactAddresses"]>[0]
> & {
  kind: "INVOICE" | "BILLING" | "SHIPPING" | "DELIVERY_NOTE" | "SAFETY_DATA";
  email: string;
  name1: string;
  partnerId: string;
};

interface GetSelectedCustomerOptions {
  customerId?: string;
  countryCode?: keyof typeof WebStoreMapping; // Uses enum keys
  webStores?: (keyof typeof WebStoreMapping)[]; // Uses enum keys
  addresses?: {
    data: AddressInput[];
    replace?: boolean; // false = add, true = replace
  };
  contactAddresses?: {
    data: ContactAddressInput[];
    replace?: boolean; // false = add, true = replace
  };
  isManaged?: boolean;
}

// Generic function to generate customer with optional addresses and contact addresses
export const getSelectedCustomer = (
  options: GetSelectedCustomerOptions = {},
): AccountEntityExpandResponse => {
  // Determine salesOrganizationId and web stores based on countryCode using enum
  const countryCode = options.countryCode ?? "DE";
  const salesOrganizationId = WebStoreMapping[countryCode].salesOrganizationId;
  const webStores = options.webStores
    ? options.webStores.map((key) => WebStoreMapping[key])
    : defaultSelectedCustomer.account.webstores;

  // Create default addresses with the appropriate salesOrganizationId
  const defaultAddressesWithSalesOrg = defaultAddresses.map((address) => ({
    ...address,
    salesOrganizationId,
  }));

  // Process addresses
  let finalAddresses = defaultAddressesWithSalesOrg;
  if (options.addresses) {
    const newAddresses = options.addresses.data.map((addressInput) => ({
      default: false,
      name1: "",
      street: "",
      streetNumber: "",
      postalCode: "",
      city: "",
      countryCode: "DE" as const,
      salesOrganizationId,
      ...addressInput,
    }));

    if (options.addresses.replace) {
      finalAddresses = newAddresses as typeof finalAddresses;
    } else {
      finalAddresses = [
        ...defaultAddressesWithSalesOrg,
        ...newAddresses,
      ] as typeof finalAddresses;
    }
  }

  // Process contact addresses
  let finalContactAddresses = defaultContactAddresses;
  if (options.contactAddresses) {
    const newContactAddresses = options.contactAddresses.data.map(
      (contactAddressInput) => ({
        name2: "",
        ...contactAddressInput,
      }),
    );

    if (options.contactAddresses.replace) {
      finalContactAddresses = newContactAddresses;
    } else {
      finalContactAddresses = [
        ...defaultContactAddresses,
        ...newContactAddresses,
      ];
    }
  }

  return {
    ...defaultSelectedCustomer,
    account: {
      ...defaultSelectedCustomer.account,
      ...(options.customerId && { accountId: options.customerId }),
      ...(options.isManaged !== undefined && { isManaged: options.isManaged }),
      webstores: webStores,
      services,
    },
    addresses: finalAddresses,
    contactAddresses: finalContactAddresses,
  };
};
